import { API_URL } from '../../util/networkEndpoints';
import { LocalizedText } from "../../components/LocalizedText";
import './Login.css';
import './AuthNinja.css';
import logo from '../../assets/pknin-logo.png';
import { isMobileGameDevice } from '../../hooks/useMobileGameViewport';
import { MobileAuthEffects } from './MobileAuthEffects';
import { MobileLoginArt } from './MobileLoginArt';

import * as React from 'react';
import { useAppDispatch } from '../../hooks';
import { LoadState, setId, setLoadState } from '../../slices/accountSlice';
import { ServerSelect } from './ServerSelect';
import { postData } from '../../util/fetch';

interface Props {
  onClose: () => void;
  initialTab?: 'home' | 'topup' | 'mall';
  onRegister?: () => void;
}

export const Login: React.FC<Props> = ({ onClose, initialTab, onRegister }) => {
  const [mobile] = React.useState(isMobileGameDevice);
  const [visiblePassword, setVisiblePassword] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const dispatch = useAppDispatch();

  const [username, setUsername] = React.useState(() => localStorage.getItem('pknin-remembered-user') || process.env.REACT_APP_DEFAULT_USERNAME || '');
  const [remember, setRemember] = React.useState(() => !!localStorage.getItem('pknin-remembered-user'));
  const [password, setPassword] = React.useState(process.env.REACT_APP_DEFAULT_PASSWORD as string);
  const [response, setResponse] = React.useState('');
  const [servers, setServers] = React.useState<{ servers: number[]; valid: number[] }>({
    servers: [],
    valid: [],
  });
  const [jwt, setJWT] = React.useState<string>();

  const onSubmit = React.useCallback(() => {
    if (busy) return;
    setBusy(true);
    dispatch(setLoadState(LoadState.LOADING));
    postData(`${API_URL}/login`, { username, password }).then((data) => {
      if (data.error) {
        dispatch(setLoadState(LoadState.NOT_LOADED));
        return setResponse(data.error);
      }

      dispatch(setId(data.id));
      if (mobile) {
        if (remember) localStorage.setItem('pknin-remembered-user', username);
        else localStorage.removeItem('pknin-remembered-user');
      }
      setJWT(data.jwt);
      setServers({
        servers: data.servers,
        valid: data.valid,
      });
    }).catch(() => {
      dispatch(setLoadState(LoadState.NOT_LOADED));
      setResponse('เชื่อมต่อไม่สำเร็จ กรุณาลองอีกครั้ง');
    }).finally(() => setBusy(false));
  }, [username, password, dispatch, busy, mobile, remember]);

  if (servers.servers?.length > 0 && jwt) {
    return <ServerSelect jwt={jwt} servers={servers.servers} valid={servers.valid} onClose={onClose} initialTab={initialTab} />;
  }

  if(mobile) return <MobileLoginArt username={username} password={password} remember={remember} visible={visiblePassword} busy={busy} error={response} onUsername={setUsername} onPassword={setPassword} onRemember={setRemember} onVisible={()=>setVisiblePassword(!visiblePassword)} onSubmit={onSubmit} onRegister={onRegister}/>;

  return (
    <div className={`login__container ninja-auth${mobile ? ' mobile-game-auth' : ''}`}>
      <div className="blur" />
      {mobile && <MobileAuthEffects />}
      <div className="login__modal" role="dialog" aria-modal="true" aria-labelledby="ninja-login-title" onKeyDown={(event) => {if(event.key === 'Enter' && event.target instanceof HTMLInputElement) {event.preventDefault();onSubmit();}}}>
        <img className="ninja-auth-logo" src={logo} alt="PkNiN" />
        <span className="ninja-auth-seal" aria-hidden="true">忍</span>
        <button id="login__close" aria-label="ปิดหน้าต่างเข้าสู่ระบบ" onClick={onClose}>
          X
        </button>

        <h2 id="ninja-login-title"><LocalizedText text={"Login"} /></h2>
        {!mobile && <p className="ninja-auth-intro">กลับสู่หมู่บ้าน แล้วออกไปสร้างตำนานของคุณ</p>}

        <div id="login__body">
          <div>
            <label htmlFor="username"><LocalizedText text={"Username:"} /></label>
            <input type="text" id="username" placeholder={mobile ? 'ชื่อผู้ใช้' : undefined} autoComplete="username" autoCapitalize="none" spellCheck={false} onChange={(e) => setUsername(e.currentTarget.value)} value={username} />
          </div>

          <div>
            <label htmlFor="password"><LocalizedText text={"Password:"} /></label>
            <span className="ninja-auth-password"><input type={visiblePassword ? 'text' : 'password'} id="password" placeholder={mobile ? 'รหัสผ่าน' : undefined} autoComplete="current-password" onChange={(e) => setPassword(e.currentTarget.value)} value={password} /><button type="button" className="ninja-auth-eye" aria-label={visiblePassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'} aria-pressed={visiblePassword} onClick={() => setVisiblePassword(!visiblePassword)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/></svg></button></span>
          </div>
        </div>
        {mobile && <div className="mobile-auth-options"><label><input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} />จดจำบัญชี</label><a href="https://www.facebook.com/pknjth/" target="_blank" rel="noopener noreferrer">ลืมรหัสผ่าน?</a></div>}

        <div id="login__response"><LocalizedText text={response} /></div>

        <div id="login__submit">
          <button onClick={onSubmit} disabled={busy}><LocalizedText text={busy ? 'กำลังเข้าสู่ระบบ…' : mobile ? 'เข้าสู่ระบบ' : 'Submit'} /></button>
        </div>
        {onRegister && <button type="button" className="ninja-auth-switch" onClick={onRegister} disabled={busy}>สมัครสมาชิก</button>}
        {mobile && <div className="mobile-auth-community"><small>ร่วมชุมชนนินจาของเรา</small><div><a href="https://discord.gg/pWMXsMaSe" target="_blank" rel="noopener noreferrer" aria-label="Discord" className="mobile-auth-discord"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19.7 5.1a18 18 0 0 0-4.4-1.4l-.5 1a16 16 0 0 0-5.6 0l-.5-1a18 18 0 0 0-4.4 1.4C1.5 9.2.8 13.1 1.2 17a18 18 0 0 0 5.4 2.7l1.1-1.8-1.6-.8.4-.3c3.5 1.6 7.5 1.6 11 0l.4.3-1.6.8 1.1 1.8a18 18 0 0 0 5.4-2.7c.5-4.5-.8-8.4-3.1-11.9ZM8.5 14.8c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Zm7 0c-1 0-1.8-.9-1.8-2s.8-2 1.8-2 1.8.9 1.8 2-.8 2-1.8 2Z"/></svg></a><a href="https://www.facebook.com/pknjth/" target="_blank" rel="noopener noreferrer" aria-label="Facebook แฟนเพจ" className="mobile-auth-facebook">f</a></div></div>}
        <p className="ninja-auth-footnote">PkNiN · เส้นทางนินจาของคุณเริ่มต้นที่นี่</p>
      </div>
    </div>
  );
};
